import React, { useEffect, useState } from 'react';
import Icon from '@ramsey-design-system/icon';
import { ErrorIcon } from '@ramsey-design-system/icons/dist/ErrorIcon';
import Text from '@ramsey-design-system/text';
import { Checkmark } from './checkmark';
import styles from './fetch-visualizer.module.css';

interface FetchVisualizerProps {
  fetchResponse: Promise<any>;
  className?: string;
  errorText?: string;
}

type FetchState = 'IN_PROGRESS' | 'DONE' | 'ERROR';

/**
 * Show this component after a fetch has been requested. Handles states of that fetch call defined in `FetchState`
 *
 * `IN_PROGRESS` -> `DONE` | `ERROR`
 *
 * After a few seconds this component hides itself
 *
 * @param param0
 * @returns
 */
const FetchVisualizer = ({
  className,
  fetchResponse,
  errorText = 'Please try again later.',
}: FetchVisualizerProps) => {
  const [fetchState, setFetchState] = useState<FetchState>('IN_PROGRESS');

  const inProgressIcon = (
    <span className={`${styles.checkmark} ${styles.skeleton}`} />
  );
  const doneIcon = <Checkmark className={styles.checkmark} />;
  const errorIcon = (
    <span className={styles.error}>
      <Icon icon={ErrorIcon} color="negative" />
      <Text size="bodySmall">{errorText}</Text>
    </span>
  );

  const stateIcon = () => {
    switch (fetchState) {
      case 'IN_PROGRESS':
        return inProgressIcon;
      case 'DONE':
        return doneIcon;
      case 'ERROR':
        return errorIcon;
      default:
        return undefined;
    }
  };

  const handlePromise = async (thePromise: Promise<any>) => {
    try {
      const result = await thePromise;
      if (result.status && result.status < 300) {
        setFetchState('DONE');
      } else {
        setFetchState('ERROR');
      }
    } catch (e) {
      setFetchState('ERROR');
      console.log(e);
    }
  };

  useEffect(() => {
    // Handle the promise on component startup
    handlePromise(fetchResponse);
  }, []);

  return <div className={className}>{stateIcon()}</div>;
};

export { FetchVisualizer };
